import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface UserData {
  email: string;
  name: string;
  picture_url: string;
  plan: string;
  credits_remaining: number;
  joined_date: string;
}

interface UseUserDataReturn {
  userData: UserData | null;
  loading: boolean;
  error: string | null;
  refetchUserData: () => Promise<void>;
  useCredit: (amount?: number) => Promise<{ balance: number; used: number }>;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const useUserData = (): UseUserDataReturn => {
  const { isAuthenticated, user } = useAuth0();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeAuthenticatedRequest = useCallback(async (url: string, options: RequestInit = {}, userData?: any) => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }

    try {
      // Use the provided userData or get it from Auth0 user object
      const userInfo = userData || user;
      
      console.log('Making request with user data:', {
        email: userInfo?.email,
        name: userInfo?.name,
        hasUserInfo: !!userInfo
      });
      
      // Prepare the request body with user data
      let requestBody;
      if (options.body && typeof options.body === 'string') {
        // If there's existing body, merge with user data
        const existingData = JSON.parse(options.body);
        requestBody = JSON.stringify({
          ...existingData,
          userData: {
            email: userInfo?.email,
            name: userInfo?.name,
            picture: userInfo?.picture,
            sub: userInfo?.sub
          }
        });
      } else {
        // If no existing body, just send user data
        requestBody = JSON.stringify({
          userData: {
            email: userInfo?.email,
            name: userInfo?.name,
            picture: userInfo?.picture,
            sub: userInfo?.sub
          }
        });
      }
      
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Content-Type': 'application/json',
        },
        body: requestBody
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API request failed:', {
          url,
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      console.error('API request failed:', err);
      throw err;
    }
  }, [isAuthenticated, user]);

  const provisionUser = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/provision', {
        method: 'POST',
      });
      return response.user;
    } catch (err) {
      console.error('Failed to provision user:', err);
      throw err;
    }
  }, [makeAuthenticatedRequest]);

  const fetchUserData = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/me', {
        method: 'POST'
      });
      return response.user;
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      throw err;
    }
  }, [makeAuthenticatedRequest]);

  const refetchUserData = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setUserData(null);
      setError(null);
      return;
    }

    // Don't proceed if we don't have essential user info from Auth0
    if (!user.email || !user.sub) {
      console.warn('User missing essential info:', { email: !!user.email, sub: !!user.sub });
      setError('User information incomplete. Please try logging in again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Fetching user data for:', { email: user.email, sub: user.sub });
      
      // First try to fetch existing user data
      try {
        const existingUser = await fetchUserData();
        console.log('Existing user found:', existingUser);
        setUserData(existingUser);
      } catch (fetchError) {
        console.log('User not found in database, provisioning new user...');
        
        // If user doesn't exist, provision them (signup case)
        const provisionedUser = await provisionUser();
        console.log('New user provisioned successfully:', provisionedUser);
        setUserData(provisionedUser);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user data';
      console.error('Error in user data flow:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, provisionUser, fetchUserData]);

  const useCredit = useCallback(async (amount: number = 1): Promise<{ balance: number; used: number }> => {
    try {
      const response = await makeAuthenticatedRequest('/api/credits/use', {
        method: 'POST',
        body: JSON.stringify({ amount }),
      });

      // Update local user data with new balance
      if (userData) {
        setUserData({
          ...userData,
          credits_remaining: response.balance
        });
      }

      return {
        balance: response.balance,
        used: response.used
      };
    } catch (err) {
      console.error('Failed to use credit:', err);
      throw err;
    }
  }, [makeAuthenticatedRequest, userData]);

  // Load user data when authentication state changes
  useEffect(() => {
    refetchUserData();
  }, [refetchUserData]);

  return {
    userData,
    loading,
    error,
    refetchUserData,
    useCredit,
  };
};
